export interface LabGem {
  name: string;
  icon?: string;
  cost: number;
  costChaos: number;
  count: number;
  colour: "red" | "green" | "blue" | "unknown";
}

export type GemColour = "red" | "green" | "blue" | "unknown";

const EXCLUDE = ["purity of", "impurity of", "of tarthus", "of trarthus"];

function isExcluded(name: string): boolean {
  const lower = name.toLowerCase();
  return EXCLUDE.some((ex) => lower.includes(ex));
}

function hasOf(name: string): boolean {
  return /\bof\b/i.test(name);
}

function attrToColour(attr: string): GemColour {
  const a = attr.toLowerCase().trim();
  if (a === "strength" || a === "str") return "red";
  if (a === "dexterity" || a === "dex") return "green";
  if (a === "intelligence" || a === "int") return "blue";
  return "unknown";
}

async function fetchColourMap(): Promise<Map<string, GemColour>> {
  const map = new Map<string, GemColour>();
  try {
    const res = await fetch("/api/gemcolours");
    const data = await res.json();
    const rows: any[] = data?.cargoquery ?? [];
    for (const row of rows) {
      const name = row?.title?.name ?? "";
      const attr = row?.title?.attr ?? "";
      if (name && attr) map.set(name.toLowerCase(), attrToColour(attr));
    }
  } catch {
    // If wiki is unreachable, return empty map (all "unknown")
  }
  return map;
}

export async function fetchLabGems(league = "Allflame"): Promise<LabGem[]> {
  const [gemsRes, colourMap] = await Promise.all([
    fetch(`/api/gems?league=${encodeURIComponent(league)}`),
    fetchColourMap(),
  ]);

  const data = await gemsRes.json();
  const lines: any[] = data.lines;

  const byName = new Map<string, any[]>();
  for (const raw of lines) {
    if (raw.corrupted) continue;
    if ((raw.gemLevel ?? 1) !== 1) continue;
    if (!hasOf(raw.name)) continue;
    if (isExcluded(raw.name)) continue;
    const q = raw.gemQuality ?? 0;
    if (q > 19) continue;

    if (!byName.has(raw.name)) byName.set(raw.name, []);
    byName.get(raw.name)!.push(raw);
  }

  const results: LabGem[] = [];

  for (const [name, entries] of byName) {
    const q0   = entries.filter((e) => (e.gemQuality ?? 0) === 0);
    const lowQ = entries.filter((e) => (e.gemQuality ?? 0) >= 1 && (e.gemQuality ?? 0) <= 19);
    const pool = q0.length > 0 ? q0 : lowQ;
    if (pool.length === 0) continue;

    const best = pool.reduce((a: any, b: any) =>
      (a.divineValue ?? 0) <= (b.divineValue ?? 0) ? a : b
    );

    const colour = colourMap.get(name.toLowerCase()) ?? "unknown";

    results.push({
      name,
      icon: best.icon,
      cost: best.divineValue ?? 0,
      costChaos: best.chaosValue ?? 0,
      count: best.count ?? 0,
      colour,
    });
  }

  results.sort((a, b) => a.name.localeCompare(b.name));
  return results;
}
